import { Tuple2 }		from "ts-base/tuple";
import { Fn }			from "ts-base/fn";
import { Maybe }		from "ts-base/maybe";
import { Equal }		from "ts-base/equal";
import { Monoid }		from "ts-base/monoid";
import { Comparator }	from "ts-base/comparator";
import { Compared }		from "ts-base/compared";

export namespace Strings {
	export const joinN	= (...items:ReadonlyArray<string>):string	=>
		join(items);

	export const join	= (items:ReadonlyArray<string>):string	=>
		joinWith(items, "");

	export const joinWith	= (items:ReadonlyArray<string>, separator:string):string	=>
		items.join(separator);

	export const mapJoin	= <T>(items:ReadonlyArray<T>, func:Fn<T, string>):string	=>
		join(items.map(func));

	export const mapJoinWith	= <T>(items:ReadonlyArray<T>, separator:string, func:Fn<T, string>):string	=>
		joinWith(items.map(func), separator);

	//-----------------------------------------------------------------------------
	//## index of

	export const firstIndexOfOrNull	= (self:string) => (search:string):number|null	=>
		indexOrNull(self.indexOf(search));

	export const lastIndexOfOrNull	= (self:string) => (search:string):number|null	=>
		indexOrNull(self.lastIndexOf(search));

	export const firstIndexOf	= (self:string) => (search:string):Maybe<number>	=>
		Maybe.fromNullable(firstIndexOfOrNull(self)(search));

	export const lastIndexOf	= (self:string) => (search:string):Maybe<number>	=>
		Maybe.fromNullable(lastIndexOfOrNull(self)(search));

	const indexOrNull	= (index:number):number|null	=>
		index !== -1 ? index : null;

	//-----------------------------------------------------------------------------

	export const splitAt = (self:string) => (index:number):Tuple2<string, string> => [
		self.substring(0, index),
		self.substring(index),
	];

	/*
	sadly, this cannot be supported in general because of these special cases:
	"".split("")				=== []
	"".split(new Regexp(""))	=== []

	// NOTE does not support the limit?:number argument to avoid confusion
	// NOTE this does not support the well-known Symbol.split overload

	export const split	= (self:string)	=> (separator:string|RegExp):Nea<string>	=>
		self.split(separator) as unknown as Nea<string>;
	*/

	export const takeLeft	= (self:string)	=> (length:number):string	=>
		self.slice(0, length);

	export const takeRight	= (self:string)	=> (length:number):string	=>
		self.slice(self.length-length, self.length);

	export const dropLeft	= (self:string)	=> (length:number):string	=>
		self.slice(length, self.length);

	export const dropRight	= (self:string)	=> (length:number):string	=>
		self.slice(0, self.length-length);

	export const cutPrefix	= (self:string)	=> (prefix:string):string|null	=>
		self.startsWith(prefix) ? dropLeft(self)(prefix.length) : null;

	export const cutSuffix	= (self:string)	=> (suffix:string):string|null	=>
		self.endsWith(suffix) ? dropRight(self)(suffix.length) : null;

	//-----------------------------------------------------------------------------
	//## typeclasses

	export const equal:Equal<string>		= Equal.builtin;
	export const compare:Comparator<string>	= Comparator.builtin;

	export const joined:Monoid<string>	= {
		empty:	"",
		concat:	(a, b) => a + b,
	};

	export const collatorCompare	= (collator:Intl.Collator):Comparator<string>	=>
		(a, b)	=> Compared.normalize(collator.compare(a, b));

	export const collatorEqual	= (collator:Intl.Collator):Equal<string>	=>
		(a, b)	=> collator.compare(a, b) === 0;

	//-----------------------------------------------------------------------------

	export type LocaleFlags	= {
		locales?:	string | Array<string>,
		options?:	Intl.CollatorOptions,
	};

	export const localeCompare	= (flags:LocaleFlags):Comparator<string>	=>
		collatorCompare(new Intl.Collator(flags.locales, flags.options));

	export const localeEqual	= (flags:LocaleFlags):Equal<string>	=>
		collatorEqual(new Intl.Collator(flags.locales, flags.options));
}
